import { forwardRef, useEffect } from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { media } from '@myrealtrip/modash';
import { IcoLocationOn, IcoTrain } from '@myrealtrip/web-icons';
import { Text } from '@myrealtrip/web-ui';

import useIntersect from '~hooks/useIntersect';
import SwitchCase from '~components/SwitchCase';
import { TRAIN_PLACE_TYPE } from '~constants/pointToPointTicket';

import type { SearchPlaceDetail } from '~models/Transportation/PointToPointTicket';

interface Props {
  searchKeyword: string;
  placeList: SearchPlaceDetail[];
  selectedIndex: number;
  selectedItemRef?: React.RefObject<HTMLLIElement>;
  onPlaceClick: (place: SearchPlaceDetail) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLUListElement>) => void;
  onFetchMore: () => void;
}

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const PlaceList = forwardRef<HTMLUListElement, Props>(
  (
    {
      searchKeyword,
      placeList,
      selectedIndex,
      selectedItemRef,
      onPlaceClick,
      onKeyDown,
      onFetchMore,
    },
    ref,
  ) => {
    const theme = useTheme();
    const { isIntersecting, subscribe } = useIntersect();

    const highlightMatch = (text: string, searchQuery: string) => {
      if (!searchQuery) return text;

      const safeQuery = escapeRegExp(searchQuery);
      const regex = new RegExp(`(${safeQuery})`, 'gi');
      const parts = text.split(regex);

      return parts.map((part, i) =>
        regex.test(part) ? (
          <Text key={i} typography="captionBold15" color={theme.colors.blue.s700}>
            {part}
          </Text>
        ) : (
          part
        ),
      );
    };

    useEffect(() => {
      if (isIntersecting) {
        onFetchMore();
      }
    }, [isIntersecting, onFetchMore]);

    if (placeList.length === 0) {
      return <NotFoundResult />;
    }

    return (
      <List ref={ref} tabIndex={0} onKeyDown={onKeyDown}>
        {placeList.map((place, index) => (
          <PlaceItem
            key={place.id}
            ref={index === selectedIndex ? selectedItemRef : null}
            selected={index === selectedIndex}
            onClick={() => onPlaceClick(place)}
          >
            <SwitchCase
              value={place.type}
              caseBy={{
                [TRAIN_PLACE_TYPE.CITY]: <IcoLocationOn />,
                [TRAIN_PLACE_TYPE.STATION]: <IcoTrain />,
              }}
            />
            <TextInfoWrapper>
              <Text
                typography="paragraphBold15"
                color={theme.colors.gray.s800}
                onClick={() => onPlaceClick(place)}
              >
                {highlightMatch(place.content.korean, searchKeyword)}
              </Text>
              <GlobalLabelText typography="captionNormal13" color={theme.colors.gray.s600}>
                {place.label}
              </GlobalLabelText>
            </TextInfoWrapper>
          </PlaceItem>
        ))}
        <InfiniteScrollTrigger ref={subscribe} />
      </List>
    );
  },
);

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  outline: none;
  padding: 0 20px 80px;

  ${media.minMobile} {
    padding: 4px 20px 40px;
  }
`;

const PlaceItem = styled.li<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  background-color: ${({ selected, theme }) => (selected ? theme.colors.gray.s100 : 'transparent')};
  border-radius: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray.s50};
  }
`;

const TextInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const NotFoundResult = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.minMobile} {
    height: 240px;
  }
`;

const GlobalLabelText = styled(Text)`
  ${media.minMobile} {
    line-height: 1;
  }
`;

const InfiniteScrollTrigger = styled.div`
  height: 1px;
`;

PlaceList.displayName = 'PlaceList';

export default PlaceList;
